import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  LinearProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = { name: '', description: '', is_active: true, sort_order: 0 };

export default function EmployeeJobRoles() {
  const { hasPermission } = useAuth();
  const canModify = hasPermission('users_modify');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/employee-job-roles');
      setRows(r.data?.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load employee job roles');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      description: row.description || '',
      is_active: row.is_active !== false,
      sort_order: Number(row.sort_order || 0)
    });
    setOpen(true);
  };

  const save = async () => {
    if (!String(form.name || '').trim()) {
      toast.error('Role name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/admin/employee-job-roles/${editing.id}`, form);
        toast.success('Employee job role updated');
      } else {
        await api.post('/admin/employee-job-roles', form);
        toast.success('Employee job role created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save employee job role');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/admin/employee-job-roles/${row.id}`);
      toast.success('Employee job role deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete employee job role');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Employee Job Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage employee role names and descriptions used in Contact onboarding.
          </Typography>
        </Box>
        {canModify && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            Add Employee Job Role
          </Button>
        )}
      </Box>

      {loading ? (
        <LinearProgress />
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell>Role Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Sort</TableCell>
                  {canModify && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canModify ? 5 : 4}>
                      <Typography color="text.secondary">No employee job roles found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Typography fontWeight={600}>{row.name}</Typography>
                      </TableCell>
                      <TableCell>{row.description || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.is_active ? 'Active' : 'Inactive'}
                          color={row.is_active ? 'success' : 'default'}
                          variant={row.is_active ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">{Number(row.sort_order || 0)}</TableCell>
                      {canModify && (
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => remove(row)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Employee Job Role' : 'Add Employee Job Role'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Role Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            multiline
            minRows={2}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Sort Order"
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm((prev) => ({ ...prev, sort_order: Number(e.target.value || 0) }))}
          />
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Active
            </Typography>
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
